import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TsTabGroupComponent } from 'tsui/lib/ts-tab-group/ts-tab-group.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { map, take } from 'rxjs/operators';
import { TsButtonModule, TsPageHeaderModule } from 'tsui';
import { Store } from '@ngxs/store';
import { AssetManagementActions } from '../../state/asset-management.actions';
import { AssetManagementState } from '../../state/asset-management.state';
import { AssetManagementRouting } from '../../asset-management.routing';
import { AssetMetadata } from '@modules/asset-management/models/asset-metadata';

@Component({
  selector: 'asset-management-container',
  standalone: true,
  imports: [TsTabGroupComponent, AsyncPipe, TsButtonModule, TsPageHeaderModule],
  template: `
    @if (asset$ | async; as assetMetadata) {
      <section class="financials-tab">
        <ts-tab-group
          mode="route"
          [tabs]="[
            { label: 'General Ledger', icon: 'ts_table', path: AssetManagementRoutes.generalLedger },
            { label: 'Trial Balance', icon: 'ts_table', path: AssetManagementRoutes.trialBalance },
            { label: 'Balance Sheet', icon: 'ts_table', path: AssetManagementRoutes.balanceSheet },
            { label: 'Income Statement', icon: 'ts_table', path: AssetManagementRoutes.incomeStatement },
            {
              label: 'Trailing Income Statement',
              icon: 'ts_table',
              path: AssetManagementRoutes.trailingIncomeStatement,
            },
            { label: 'Budget', icon: 'ts_calculator', path: AssetManagementRoutes.budget },
          ]"
        />
      </section>
    }
  `,
  styleUrls: [`./asset-management-container.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetManagementContainerComponent implements OnDestroy, OnInit {
  readonly route = inject(ActivatedRoute);
  readonly store = inject(Store);
  protected readonly AssetManagementRoutes = AssetManagementRouting;
  readonly router = inject(Router);

  asset$ = this.store.select(AssetManagementState.asset);

  ngOnInit() {
    this.store.dispatch(new AssetManagementActions.Clear());

    this.route.params
      .pipe(map(AssetManagementContainerComponent.extractRelatedToMetadata), take(1))
      .subscribe(({ related_to_id, related_to_type }: AssetMetadata) => {
        this.store.dispatch(new AssetManagementActions.SetAssetMetadata({ related_to_id, related_to_type }));
        // navigate to general ledger
        this.router.navigate([AssetManagementRouting.generalLedger], { relativeTo: this.route });
      });
  }

  static extractRelatedToMetadata(params: Params) {
    const id = Number(params['id']);

    // `id` is used in pre-jupiter modules and assume we are dealing with a property
    if (!isNaN(id)) {
      return { related_to_id: id, related_to_type: 'Property' };
    }

    const recordId = Number(params['recordId']);
    const relatedToKey = params['relatedToKey'];

    // `recordId` is used in jupiter modules
    if (!isNaN(recordId) && AssetManagementRouting.routeToObjectMap[relatedToKey]) {
      return { related_to_id: recordId, related_to_type: AssetManagementRouting.routeToObjectMap[relatedToKey] };
    }

    console.error(`Invalid asset metadata for ${recordId} and ${relatedToKey}`);
    return null;
  }

  ngOnDestroy() {
    this.store.dispatch(new AssetManagementActions.Clear());
  }
}
